import React, { useState, useCallback } from 'react';
import { Zap, Users, Image, DollarSign, TrendingUp, AlertTriangle, Loader2, RefreshCw, XCircle, CheckCircle2 } from 'lucide-react';
import { getAuthHeader } from '../utils/auth';

export default function MetaOptimizationPanel({ accountId, accountName, addToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOptimization = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/meta/optimize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ account_id: accountId })
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Failed');
      setData(await res.json());
    } catch (err) {
      setError(err.message);
      addToast?.({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  }, [accountId, addToast]);

  if (!data && !loading && !error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#6366f1]/10 flex items-center justify-center">
          <Zap className="w-6 h-6 text-[#6366f1]" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-white" style={{fontFamily:'Manrope,sans-serif'}}>Meta Ads AI Analysis</p>
          <p className="text-xs text-[#71717a] mt-1">Get optimization recommendations for {accountName}</p>
        </div>
        <button onClick={fetchOptimization} className="flex items-center gap-2 px-4 py-2 bg-[#6366f1] hover:bg-[#5558e8] text-white text-sm rounded-xl transition-colors">
          <Zap className="w-3.5 h-3.5" /> Analyze Account
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="w-6 h-6 text-[#6366f1] animate-spin" />
        <p className="text-sm text-[#71717a]">Analyzing Meta campaigns with AI...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <XCircle className="w-6 h-6 text-red-400" />
        <p className="text-sm text-[#71717a]">{error}</p>
        <button onClick={fetchOptimization} className="text-xs text-[#6366f1] hover:underline">Try again</button>
      </div>
    );
  }

  const s = data?.suggestions || {};
  const stats = data?.stats || {};

  return (
    <div className="space-y-3">
      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'CPR', value: `₹${stats.cpr?.toFixed(0)}`, color: stats.cpr <= 300 ? '#22c55e' : '#ef4444' },
          { label: 'CPM', value: `₹${stats.cpm?.toFixed(0)}`, color: '#a1a1aa' },
          { label: 'CTR', value: `${stats.ctr}%`, color: stats.ctr >= 1 ? '#22c55e' : '#ef4444' }
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-[#111114] border border-[#27272a] rounded-xl p-3 text-center">
            <p className="text-xs text-[#52525b] mb-1">{label}</p>
            <p className="text-sm font-mono font-bold" style={{ color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Health Assessment */}
      {s.health_assessment && (
        <div className="bg-[#6366f1]/5 border border-[#6366f1]/20 rounded-xl px-4 py-3">
          <p className="text-xs text-[#71717a] leading-relaxed">{s.health_assessment}</p>
        </div>
      )}

      {/* Priority Actions */}
      {s.priority_actions?.length > 0 && (
        <div className="border border-[#27272a] rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-[#111114] flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-white" style={{fontFamily:'Manrope,sans-serif'}}>Priority Actions</span>
          </div>
          <div className="px-4 py-3 bg-[#09090B] space-y-2">
            {s.priority_actions.map((a, i) => (
              <div key={i} className="border border-amber-500/20 bg-amber-500/5 rounded-xl px-3 py-2.5">
                <p className="text-sm font-medium text-white mb-1">{a.action}</p>
                <p className="text-xs text-[#71717a]">{a.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audience Recommendations */}
      {s.audience_recommendations?.length > 0 && (
        <div className="border border-[#27272a] rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-[#111114] flex items-center gap-2">
            <Users className="w-4 h-4 text-[#6366f1]" />
            <span className="text-sm font-medium text-white" style={{fontFamily:'Manrope,sans-serif'}}>Audience Strategy</span>
          </div>
          <div className="px-4 py-3 bg-[#09090B] space-y-1.5">
            {s.audience_recommendations.map((r, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#6366f1] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[#a1a1aa]">{r}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Creative Recommendations */}
      {s.creative_recommendations?.length > 0 && (
        <div className="border border-[#27272a] rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-[#111114] flex items-center gap-2">
            <Image className="w-4 h-4 text-purple-400" />
            <span className="text-sm font-medium text-white" style={{fontFamily:'Manrope,sans-serif'}}>Creative Recommendations</span>
          </div>
          <div className="px-4 py-3 bg-[#09090B] space-y-1.5">
            {s.creative_recommendations.map((r, i) => (
              <div key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-purple-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[#a1a1aa]">{r}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Next Plan of Action */}
      {s.next_poa?.length > 0 && (
        <div className="border border-[#27272a] rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-[#111114] flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#22c55e]" />
            <span className="text-sm font-medium text-white" style={{fontFamily:'Manrope,sans-serif'}}>Next Plan of Action</span>
          </div>
          <div className="px-4 py-3 bg-[#09090B] space-y-2">
            {s.next_poa.map((step, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-full bg-[#22c55e]/10 border border-[#22c55e]/30 text-[#22c55e] text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5 font-mono">{i+1}</span>
                <p className="text-xs text-[#a1a1aa] leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scaling Opportunities */}
      {s.scaling_opportunities && (
        <div className="bg-[#22c55e]/5 border border-[#22c55e]/20 rounded-xl px-4 py-3">
          <div className="flex items-center gap-2 mb-1.5">
            <DollarSign className="w-3.5 h-3.5 text-[#22c55e]" />
            <p className="text-xs font-medium text-[#22c55e]">Scaling Opportunities</p>
          </div>
          <p className="text-xs text-[#71717a]">{s.scaling_opportunities}</p>
        </div>
      )}

      {/* Refresh Button */}
      <button onClick={fetchOptimization} disabled={loading} className="w-full flex items-center justify-center gap-2 py-2.5 border border-[#27272a] hover:border-[#6366f1]/40 hover:bg-[#6366f1]/5 rounded-xl text-xs text-[#71717a] hover:text-white transition-colors">
        <RefreshCw className="w-3.5 h-3.5" /> Refresh Analysis
      </button>
    </div>
  );
}
